import React, { useEffect } from 'react';

import { ContentWrap } from '@rockerbox/styleguide';
import ButterToast from 'butter-toast';
import { Route } from 'react-router-dom';

import ConnectedData from './data';
import GlobalApplicationData from './globalApplicationData';
import * as routes from '../../routes';
import { MarketingQA, ModelExplorer, ImpressionQA, WebhooksQA } from '../../V3/components/StreamlitTools';
import Account from '../Account';
import Announcements from '../Announcements';
import AttributableEvent from '../AttributableEvent';
import MappedEvents from '../AttributableIndex';
import ExcludeEvents from '../AttributableIndex/exclude';
import MappedOverview from '../AttributableOverview';
import BatchConfigIndex from '../BatchConfig';
import BatchConfigEdit from '../BatchConfig/BatchEdit';
import BatchInboxView from '../BatchConfig/InboxMessages';
import BatchConfigProcessedView from '../BatchConfig/ProcessedView';
import ChannelGroups from '../ChannelGroups';
import Communications from '../Communications';
import ConversionEventIndex from '../ConversionIndex';
import ConversionBackfill from '../ConversionIndex/DatasetEvaluation';
import ConversionSegment from '../ConversionSegment';
import ViewConversionSegment from '../ConversionSegment/view';
import CreativeDataSource from '../CreativeDataSource/index';
import CrossDeviceCreationIndex from '../CrossDevice/CrossDeviceCreation';
import CrossDeviceEditIndex from '../CrossDevice/CrossDeviceEdit';
import Datasets from '../Datasets';
import DataSources from '../DataSources';
import AddressDataSource from '../DataSources/AddressDataSource';
import ClickDataSource from '../DataSources/ClickDataSource';
import CustomerDataSource from '../DataSources/CustomerDataSource';
import ExternalDataSource from '../DataSources/ExternalDataSource';
import PixelDataSource from '../DataSources/PixelDataSource';
import TemporalDataSource from '../DataSources/TemporalDataSource';
import DestinationIndex from '../Destination';
import OutboxSetup from '../Destination/OutboxSetup';
import EventTags from '../EventTags';
import Facebook from '../Facebook';
import FacebookAds from '../Facebook/Ads';
import FacebookComparison from '../Facebook/Comparison';
import GlobalErrorBoundary from '../GlobalErrorBoundary';
import IntegrationsIndex from '../Integrations';
import MappingChecker from '../MappingChecker';
import Model from '../Model';
import ModelValidation from '../ModelValidation';
import Navigation from '../Navigation';
import Notifications from '../Notifications';
import OTTView from '../OTT';
import PixelLive from '../PixelLive';
import PixelView from '../Pixels';
import PostlogLift from '../PostlogLift';
import PromoResults from '../PromoCodes';
import ReportCustomize from '../ReportCustomize';
import ReportStatus from '../ReportDeliveryStatus';
import ReportIndex from '../ReportIndex';
import EditorForm from '../ReportIndex/EditorForm';
import ReportInteractive from '../ReportInteractive/index';
import ReportSchema from '../ReportSchema';
import Shopify from '../Shopify';
import { Aux } from '../stateless';
import TrackPageChange from '../TrackPageChange';
import TransformTables from '../TransformTables';
import TriggerIndex from '../Triggers';
import WebhooksIndex from '../Webhooks';
import WebhooksConfig from '../Webhooks/WebhooksConfig';

const routeConfig = [
  // <----------------- PARENTS ---------------------->
  { name: 'settings', displayName: 'Settings', iconName: 'cogs', route: '/v2/settings', lite: true },
  { name: 'channels', displayName: 'Channels', iconName: 'sitemap', route: '/v2/channels', lite: true },
  { name: 'model', displayName: 'Model', iconName: 'flask', route: '/v2/models' },
  { name: 'data', displayName: 'Data', iconName: 'database', route: '/v2/admin', lite: true },
  { name: 'tools', displayName: 'Tools', iconName: 'wrench', route: '/v2/tools', lite: true },

  // <----------------- Children of Tools -------------->
  { name: 'Marketing QA', icon: false, route: routes.marketingQA, parent: '/v2/tools', lite: true },
  { name: 'Impression QA', icon: false, route: routes.impressionQA, parent: '/v2/tools', lite: true },
  { name: 'Webhooks QA', icon: false, route: routes.webhooksQA, parent: '/v2/tools', lite: true },
  { name: 'Model Explorer', icon: false, route: routes.modelExplorer, parent: '/v2/tools', lite: true },

  // <----------------- Children of Settings -------------->
  { name: 'Account', icon: false, route: routes.account, parent: '/v2/settings', lite: true },
  { name: 'Integrations', icon: false, route: routes.integrationsIndex, parent: '/v2/settings', lite: true },
  { name: 'Conversions', icon: false, route: routes.conversionEventIndex, parent: '/v2/settings', lite: true },
  { name: 'OTT', icon: false, route: routes.ottIndex, parent: '/v2/settings' },
  { name: 'Postlog', icon: false, route: routes.postlog, parent: '/v2/settings' },
  { name: 'Datasets', icon: false, route: routes.datasetsIndex, parent: '/v2/settings', lite: true },
  { name: 'Exports', icon: false, route: routes.reports, parent: '/v2/settings', lite: true },
  { name: 'Destinations', icon: false, route: routes.destinationIndex, parent: '/v2/settings' },

  // <----------------- Children of Channels -------------->
  // { name: "Overview", icon: false, route: routes.mappedEventOverview, parent: "/v2/channels", lite: true },
  { name: 'Rules', icon: false, route: routes.mappedEventIndex, parent: '/v2/channels', lite: true },
  { name: 'Exclusions', icon: false, route: routes.excludedEventIndex, parent: '/v2/channels', lite: true },
  { name: 'Transform Tables', icon: false, route: routes.transformTablesIndex, parent: '/v2/channels', lite: true },
  { name: 'Groups', icon: false, route: routes.channelGroups, parent: '/v2/channels', lite: true },
  { name: 'Tags', icon: false, route: routes.eventTagsIndex, parent: '/v2/channels' },
  { name: 'Promo Codes', icon: false, route: routes.promoResults, parent: '/v2/channels' },
  { name: 'Mapping Checker', icon: false, route: routes.mappingChecker, parent: '/v2/channels' },

  // <----------------- Children of Models -------------->
  { name: 'Results', icon: false, route: routes.modelsView, parent: '/v2/models' },
  { name: 'Datasets', icon: false, route: routes.modelsValidation, parent: '/v2/models' },

  // <----------------- Children of Data (aka admin) -------------->
  { name: 'Pixels', icon: false, route: routes.pixelView, parent: '/v2/admin', lite: true },
  { name: 'Pixel Status', icon: false, route: routes.pixelLive, parent: '/v2/admin', lite: true },
  { name: 'Webhooks', icon: false, route: routes.webhooksIndex, parent: '/v2/admin', lite: true },
  { name: 'Batch', icon: false, route: routes.batchConfigIndex, parent: '/v2/admin', lite: true },
  { name: 'Triggers', icon: false, route: routes.triggerIndex, parent: '/v2/admin', lite: true },
  { name: 'Data Sources', icon: false, route: routes.dataSources, parent: '/v2/admin' },
];

const Navbar = () => <Navigation allRoutes={routeConfig} />;
const V2 = props => {
  useEffect(() => {
    document.body.style.backgroundColor = '#F3F3F5';
  }, []);
  const { location } = props;
  const { pathname } = location;

  return (
    <Aux className="App rockerbox">
      <GlobalApplicationData />
      <ConnectedData />
      <ButterToast trayPosition="bottom-left" />
      <TrackPageChange path={pathname} />
      <Route path="/" component={Navbar} />
      <ContentWrap>
        <Announcements />
        <Communications />
        <Notifications />
        <GlobalErrorBoundary>
          {/** ******** SETTINGS ************ */}
          <Route exact path={`${routes.account}/:index?/:menuItem?`} component={Account} />
          <Route exact path={`${routes.conversionEventBackfill}/:startDate?/:endDate?/:datasetType?/:segmentType?`} component={ConversionBackfill} />
          <Route exact path={routes.conversionEventIndex} component={ConversionEventIndex} />
          <Route exact path={routes.createConversionEvent} component={ConversionSegment} />
          <Route path={routes.datasetsIndex} component={Datasets} />
          <Route exact path={`${routes.destinationIndex}/:id?`} component={DestinationIndex} />
          <Route exact path={`${routes.destinationIndex}/id/:id`} component={OutboxSetup} />
          <Route exact path={`${routes.editConversionEvent}/:id`} component={ConversionSegment} />
          <Route exact path={`${routes.integrationsIndex}/:platform?/:feature?/:startDate?/:endDate?`} component={IntegrationsIndex} />
          <Route exact path={`${routes.postlog}/:id?/:startDate?/:endDate?`} component={PostlogLift} />
          <Route exact path={`${routes.reportCustomize}/:id`} component={ReportCustomize} />
          <Route exact path={`${routes.reportInteractive}/:id`} component={ReportInteractive} />
          <Route exact path={`${routes.reports}/:category?/:id?`} component={ReportIndex} />
          <Route exact path={`${routes.reportSchema}/:id`} component={ReportSchema} />
          <Route exact path={`${routes.reportStatus}/:id`} component={ReportStatus} />
          <Route exact path={`${routes.savedReportEditor}/:id`} component={EditorForm} />
          <Route exact path={`${routes.ottIndex}/event/create`} component={CrossDeviceCreationIndex} />
          <Route exact path={`${routes.ottIndex}/event/edit/:id?`} component={CrossDeviceEditIndex} />
          <Route exact path={routes.ottIndex} component={OTTView} />
          <Route exact path={`${routes.viewConversionEvent}/:id`} component={ViewConversionSegment} />

          {/** ******** ADMIN STREAMLIT TOOLS ************ */}
          <Route exact path={routes.marketingQA} component={MarketingQA} />
          <Route exact path={routes.modelExplorer} component={ModelExplorer} />
          <Route exact path={routes.impressionQA} component={ImpressionQA} />
          <Route exact path={routes.webhooksQA} component={WebhooksQA} />
          {/** ******** CHANNELS ************ */}
          <Route path={routes.channelGroups} component={ChannelGroups} />
          <Route exact path={`${routes.copyMappedEvent}/:id/:urlId?`} component={AttributableEvent} />
          <Route exact path={`${routes.createExcludedEvent}/:urlId?`} component={AttributableEvent} />
          <Route exact path={`${routes.createMappedEvent}/:urlId?/:t1?/:t2?`} component={AttributableEvent} />
          <Route exact path={`${routes.editExcludedEvent}/:id/:urlId?`} component={AttributableEvent} />
          <Route exact path={`${routes.editMappedEvent}/:id/:urlId?`} component={AttributableEvent} />
          <Route exact path={routes.excludedEventIndex} component={ExcludeEvents} />
          <Route path={routes.eventTagsIndex} component={EventTags} />
          <Route exact path={routes.mappedEventIndex} component={MappedEvents} />
          <Route exact path={`${routes.mappedEventOverview}/:id?`} component={MappedOverview} />
          <Route exact path={routes.mappingChecker} component={MappingChecker} />
          <Route exact path={routes.promoResults} component={PromoResults} />
          <Route path={routes.transformTablesIndex} component={TransformTables} />

          {/** ******** MODELS ************ */}
          <Route exact path={`${routes.modelsValidation}/:featuredId?/:touchpointId?/:datasetName?/:startDate?/:endDate?/:viewDate?`} component={ModelValidation} />
          <Route exact path={`${routes.modelsView}/:id?/:run_id?/:view?/:comparisonId?`} component={Model} />

          {/** ******** ADMIN ************ */}
          <Route exact path={`${routes.batchConfigCreate}/:inboxId?`} component={BatchConfigEdit} />
          <Route exact path={`${routes.batchConfigEdit}/:id/:results?`} component={BatchConfigEdit} />
          <Route exact path={routes.batchConfigIndex} component={BatchConfigIndex} />
          <Route exact path={`${routes.batchConfigIndex}/index/:inboxId?`} component={BatchConfigIndex} />
          <Route exact path={`${routes.batchConfigView}/:id`} component={BatchConfigProcessedView} />
          <Route exact path={`${routes.batchInboxView}/:inboxId`} component={BatchInboxView} />
          <Route exact path={routes.dataSources} component={DataSources} />
          <Route exact path={`${routes.dataSources}/view/pixel_data`} component={PixelDataSource} />
          <Route exact path={`${routes.dataSources}/view/external_data`} component={ExternalDataSource} />
          <Route exact path={`${routes.dataSources}/view/creative_data/:firstMenu?/:secondMenu?/:startDate?/:endDate?`} component={CreativeDataSource} />
          <Route exact path={`${routes.dataSources}/view/click_data`} component={ClickDataSource} />
          <Route exact path={`${routes.dataSources}/view/customer_data`} component={CustomerDataSource} />
          <Route exact path={`${routes.dataSources}/view/address_data`} component={AddressDataSource} />
          <Route exact path={`${routes.dataSources}/view/temporal_data/:startDate?/:endDate?`} component={TemporalDataSource} />
          <Route exact path={`${routes.dataSources}/view/shopify`} component={Shopify} />
          <Route exact path={`${routes.dataSources}/view/fb_data`} component={Facebook} />
          <Route exact path={`${routes.dataSources}/view/fb_data/synthetic/:type/:id?/:agg?`} component={FacebookComparison} />
          <Route exact path={`${routes.dataSources}/view/fb_data/ads_data/:type/:id?`} component={FacebookAds} />
          <Route exact path={routes.pixelLive} component={PixelLive} />
          <Route exact path={routes.pixelView} component={PixelView} />
          <Route path={routes.triggerIndex} component={TriggerIndex} />
          <Route exact path={`${routes.webhooksCreate}/:id`} component={WebhooksConfig} />
          <Route exact path={`${routes.webhooksEdit}/:id/:messageId?/:results?`} component={WebhooksConfig} />
          <Route exact path={routes.webhooksIndex} component={WebhooksIndex} />
        </GlobalErrorBoundary>
      </ContentWrap>
    </Aux>
  );
};

export default V2;
